import { CloseIcon, EditIcon, SearchIcon, DeleteIcon, WarningTwoIcon } from "@chakra-ui/icons";
import {
    Box,
    Button,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Flex,
    HStack,
    Heading,
    IconButton,
    Image,
    Input,
    InputGroup, InputRightElement,
    Link,
    Text,
    VStack,
    useDisclosure,
    useToast
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { Pagination } from "components/Pagination";
import { useApi } from "hooks/useApi";
import { useAuthentication } from "hooks/useAuthentication";
import { useCache } from "hooks/useCache";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactTable } from "../../components/ReactTable";
import { showToast, toastTypes } from "utils";

type Table3RowType = {
  id: number;
  name: string;
  category_name:string;
  actionHeader: undefined;
};

export default function SubCategoryManagementSection() {
  const navigate = useNavigate();
  const {hasPermission} = useAuthentication()
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef<any>();
  const [activeItem, setActiveItem] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const toast = useToast();
  const [searchBarValue34, setSearchBarValue34] = useState("");
  const { getSubCategories, deleteSubCategory } = useApi();
  const pageIndex = 1;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const {
    data: subCategories,
    mutate
  } = useCache("subCategories", () => getSubCategories(page, searchBarValue34));
  const handleSearch = (event: any) => {
    const value = event.target.value
    mutate(getSubCategories(page,value))
  };
  const handleClearSearch = (event: any) => {
    setSearchBarValue34("");
    mutate(getSubCategories(page,""))
  }
  const handleJumpToPage = (pg: number) => {
    setPage(pg);
    mutate(getSubCategories(pg));
  };
  const handleSetNextPage = () => {
    if (subCategories.length > 0 && subCategories[0].num_of_pages >= page + 1) {
      setPage((prev: any) => prev + 1);
      mutate(getSubCategories(page + 1));
    }
  };
  const handleSetPrevPage = () => {
    if (subCategories.length > 0 && page > 0) {
      setPage((prev: any) => prev - 1);
      mutate(getSubCategories(page + 1));
    }
  };
  const table3Columns = useMemo(() => {
    const table3ColumnHelper = createColumnHelper<Table3RowType>();
    return [
      table3ColumnHelper.accessor("name", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1}  px={"25px"} py="14px">
            <Heading as="h6">Sous-Catégorie</Heading>
          </Flex>
        ),
        meta: { width: "190px" },
      }),
      table3ColumnHelper.accessor("category_name", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1}  px={"25px"} py="14px">
            <Heading as="h6">Catégorie</Heading>
          </Flex>
        ),
        meta: { width: "190px" },
      }),
      table3ColumnHelper.accessor("id", {
        cell: (info) => (
          <HStack gap={"2px"} width={"100%"} justifyContent={"flex-end"}>
            {hasPermission("subcategory", "update") && <IconButton
              variant="outline"
              colorScheme="gray_stroke_0"
              aria-label="Edit"
              size={"sm"}
              icon={<EditIcon />}
              borderRadius={"4px"}
              onClick={() => navigate(`edit/${info.getValue()}`)}
            />}
            {hasPermission("subcategory", "delete") && (
              <IconButton
                variant="outline"
                colorScheme="red_origin"
                aria-label="delete"
                size={"sm"}
                icon={<DeleteIcon />}
                borderRadius={"4px"}
                onClick={() => {
                  setActiveItem(info.row.original);
                  onConfirmOpen();
                }}
                isDisabled={isDeleting && activeItem?.id === info.row.original.id}
                isLoading={isDeleting && activeItem?.id === info.row.original.id}
              />
            )}
          </HStack>
        ),
        header: (info) => (
          <Flex px="15px" py="14px" justifyContent={"flex-end"}>
            <Heading as="h6">Action</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
    ];
  }, [navigate, hasPermission]);

  return (
    <>
      {/* dealer management section */}
      <Flex
        flexDirection="column"
        alignItems="start"
        px={{ base: "20px", sm: "24px" }}
      >
        <Flex gap="4px" alignSelf="stretch" flexDirection="column">
          <Flex
            justifyContent="center"
            alignItems="center"
            flexDirection={{ base: "column", sm: "row" }}
          >
            <Heading size="text2xl" as="h5" fontFamily="Arimo">
            Sous-Catégorie
            </Heading>
            <Flex
              pl="56px"
              pr="130px"
              gap="13px"
              flex={1}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf={{ base: "stretch", sm: "auto" }}
              flexDirection={{ base: "column", sm: "row" }}
              px={{ md: 0, base: "20px" }}
            >

              {hasPermission("subcategory", "create") && <Button
                size="xl"
                variant="outline"
                colorScheme="red_origin"
                leftIcon={
                  <Image
                    src="images/img_handshake_deal_svgrepo_com_red_origin.svg"
                    alt="Handshake-deal Svgrepo.com"
                  />
                }
                fontWeight={700}
                gap="8px"
                minW="128px"
                borderRadius="8px"
                as={Link}
                href="/sub-categories/add"
              >
                Créer une Sous-Catégorie
              </Button>}
            </Flex>
          </Flex>
        </Flex>
        <Box
          mt="16px"
          bg="white"
          w={{ md: "100%", base: "100%" }}
          borderRadius="10px"
        >
          <Flex
            bg="white"
            justifyContent="center"
            alignItems="center"
            px="12px"
            py="16px"
            flexDirection={{ md: "row", base: "column" }}
          >
            <Flex
              flex={1}
              alignItems="center"
              alignSelf={{ md: "auto", base: "stretch" }}
              flexDirection={{ base: "column", sm: "row" }}
            >
              <InputGroup w={{ base: "100%", sm: "30%" }}>
                
                <Input
                  placeholder={`Rechercher`}
                  value={searchBarValue34}
                  onChange={(event:any) => setSearchBarValue34(event.currentTarget.value)}
                  gap="8px"
                  borderWidth="1px"
                  borderRadius="8px"
                />
                <InputRightElement>
                  {searchBarValue34?.length > 0 ? (
                    <CloseIcon onClick={handleClearSearch} />
                  ) : null}
                </InputRightElement>
              </InputGroup>
                <IconButton onClick={handleSearch} ml={2} borderRadius={"10px"} py={"19px"} border={"1px solid #e2e4e6"} aria-label="search" icon={<SearchIcon/>} justifyContent={"center"} alignItems={"center"} />
            </Flex>
            <Image
              src="images/img_dot_menu.svg"
              alt="Menu Image"
              h="24px"
              w={{ md: "24px", base: "100%" }}
            />
          </Flex>
          <ReactTable
            size="sm"
            headerCellProps={{ bg: "white" }}
            tableProps={{
              borderColor: "neutral.600",
              borderTopWidth: "1px",
              borderStyle: "solid",
            }}
            columns={table3Columns as any}
            data={subCategories}
          />
          <Pagination
            pageIndex={pageIndex}
            pageSize={pageSize}
            setNextPageIndex={handleSetNextPage}
            setPrevPageIndex={handleSetPrevPage}
            setPageSize={setPageSize}
            jumpToPage={handleJumpToPage}
            numberOfPages={subCategories && subCategories?.length > 0 && subCategories[0].num_of_pages}
          />
        </Box>
      </Flex>
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" display="flex" alignItems="center" gap={2}>
              <WarningTwoIcon color="red.400" />
              Confirmation de suppression
            </AlertDialogHeader>

            <AlertDialogBody fontSize="xl" color="gray.700" textAlign="center">
              <VStack spacing={3} align="center">
                <WarningTwoIcon boxSize={16} color="red.400" />
                <Text fontSize="xl">
                  Voulez-vous vraiment supprimer la sous-catégorie « {activeItem?.name} » ?
                </Text>
                <Text fontSize="xl">Cette action est irréversible.</Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose} variant="outline" colorScheme="gray_stroke_0" leftIcon={<CloseIcon boxSize={3} />} borderRadius="8px" isDisabled={isDeleting}>
                Annuler
              </Button>
              <Button colorScheme="red_origin" ml={3} leftIcon={<DeleteIcon />} borderRadius="8px" isLoading={isDeleting} loadingText="Suppression..." onClick={async () => {
                if (!activeItem?.id) return;
                try {
                  setIsDeleting(true);
                  await deleteSubCategory(activeItem.id);
                  showToast({ toast, message: "Sous-catégorie supprimée avec succès", status: toastTypes.success });
                  onConfirmClose();
                  mutate(getSubCategories(page, searchBarValue34));
                } finally {
                  setIsDeleting(false);
                }
              }}>
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
